/**
 * Frage wartet, Seite im Hintergrund (22.10.2026, Martin 08:12, Desktop/Chrome).
 *
 * Vespera endete mit einer Frage, die Stufe sagte `entscheid=offen grund=frage` — aber die App-Seite war nicht sichtbar (Martin war im
 * Kanal-Tab). Das Mikro blieb richtig zu (`[ptt] nach-frage übersprungen hintergrund`, app.ts): ein Mikro geht in einer Hintergrund-Seite
 * nie von selbst auf. Nur: Martin sah nicht, dass Vespera wartet, und beim Zurückkommen geschah nichts.
 *
 * Jetzt:
 *  1. Hinweis: solange die Seite im Hintergrund ist und die Frage wartet, trägt der Tab-Titel `WARTET_PRAEFIX`. Kein Ton, keine
 *     Benachrichtigung, keine Berechtigung. Zurück beim Sichtbarwerden, beim Löschen und nach der Frist.
 *  2. Rückkehr: wird die Seite wieder sichtbar UND hat sie den Fokus, versucht das Mikro es GENAU EINMAL — über denselben Weg wie das
 *     Frage-Mikro (app.ts: mikroNachAntwort, schmiedOeffnenGilt, nachAusklang, startPttHandsFree mit allen Wächtern). Der Merker ist mit
 *     dem ersten Versuch verbraucht, auch wenn ein Wächter zu sagt.
 *
 * ⚠️ Nur eine schlichte Frage Vesperas (`grund=frage`, keine Rückfrage-Karte/Ja-Tor/Runde/Entwickler-Anrede — app.ts `mikroEntscheidungOffen`).
 * ⚠️ Im Zweifel zu: jede Sperre (Frist, neuer Zug, „Fertig“, Aufnahme, Stimme, Anruf, offene Karte, Entscheidung, Tippen) verbraucht den
 *    Merker ohne Öffnen. Dieses Modul öffnet nie selbst etwas; es sagt nur „versuchen“ oder „zu, weil …“.
 * ⚠️ Mehrgeräte (Sicherheitsprüfung 22.10.): antwortet Martin anderswo (Telefon, Kanal) oder nimmt ein anderes Gerät auf, ist die Frage
 *    dort beantwortet — app.ts löscht den Merker (`antwortAnderswo`, `aufnahmeAnderswo`), die Rückkehr öffnet dann nichts.
 */
import { istEigenesPttEcho } from "../../shared/geraet-kennung.js";
import { offeneJaKarte } from "./ja-karte-offen.js";

/** Ereignis (window): das Frage-Mikro entfiel, weil die Seite im Hintergrund ist — app.ts merkt die Frage für die Rückkehr. */
export const FRAGE_IM_HINTERGRUND = "voice-v8:frage-im-hintergrund";
/** So lange nach der Frage darf eine Rückkehr das Mikro noch öffnen. */
export const FRAGE_RUECKKEHR_MS = 5 * 60 * 1000;
/** Vor dem Tab-Titel, solange die Frage im Hintergrund wartet. */
export const WARTET_PRAEFIX = "● Vespera wartet – ";

/** Was beim Zurückkommen gilt — app.ts liest es aus dem Gerät. */
export interface RueckkehrLage {
  /** `document.visibilityState === "visible"` */
  sichtbar: boolean;
  /** `document.hasFocus()` — die Seite ist vorn. */
  fokus: boolean;
  zug: number;
  zugFertig: boolean;
  fertig: boolean;
  /** `pttState === "idle"` */
  ruhe: boolean;
  /** Eine Stimme klingt oder steht in der Schlange. */
  stimme: boolean;
  /** Anruf verbunden oder Modus „Anruf“. */
  anruf: boolean;
  /** Eine Ja/Nein-Karte steht offen (`jaKarteOffen`). */
  karteOffen: boolean;
  /** Inzwischen wartet eine Entscheidung (Karte, Ja-Tor, Runde, Entwickler) — app.ts `mikroEntscheidungOffen`. */
  entscheidungOffen: boolean;
  tippt: boolean;
}

export type RueckkehrErgebnis =
  | { tun: "nichts" }
  | { tun: "versuchen"; zug: number }
  | { tun: "zu"; grund: string };

interface Titel {
  lies(): string;
  setze(t: string): void;
}

interface Uhr {
  jetzt(): number;
  setTimeout(fn: () => void, ms: number): unknown;
  clearTimeout(h: unknown): void;
}

const echteUhr: Uhr = {
  jetzt: () => Date.now(),
  setTimeout: (fn, ms) => globalThis.setTimeout(fn, ms),
  clearTimeout: (h) => globalThis.clearTimeout(h as ReturnType<typeof setTimeout>),
};

/** Steht im Chat eine Ja/Nein-Karte offen? Die Prüfung liegt in lib/ja-karte-offen.ts (eine Quelle). */
export function jaKarteOffen(chat: ParentNode, jetzt: number): boolean {
  return offeneJaKarte(chat, jetzt) !== null;
}

/**
 * Kam aus dem Kanal eine Nachricht von Martin, die NICHT das eigene Sprechtasten-Echo dieser Seite ist (anderes Gerät, Telegram, Kanal)?
 * Dann ist die wartende Frage anderswo beantwortet. Eine getippte Nachricht dieser Seite ohne Kennung zählt mit — lieber zu.
 */
export function antwortAnderswo(m: { role: string; source?: string; geraet?: string | null }, eigeneKennung: string): boolean {
  if (m.role !== "user") return false;
  if (istEigenesPttEcho(m, eigeneKennung)) return false;
  return m.geraet !== eigeneKennung;
}

/** Meldet der Kanal, dass ein ANDERES Gerät des Kontos gerade aufnimmt? (Ende einer Aufnahme und die eigene zählen nicht.) */
export function aufnahmeAnderswo(e: { geraet: string; aufnahme: boolean }, eigeneKennung: string): boolean {
  return e.aufnahme && e.geraet !== eigeneKennung;
}

export class FrageRueckkehr {
  private merker: { zug: number; um: number; frist: unknown } | null = null;
  private alterTitel: string | null = null;

  constructor(
    private readonly titel: Titel,
    private readonly melde: (zeile: string) => void = () => {},
    private readonly uhr: Uhr = echteUhr,
    private readonly fristMs: number = FRAGE_RUECKKEHR_MS,
  ) {}

  /** Wartet eine Frage auf die Rückkehr? */
  wartet(): boolean {
    return this.merker !== null;
  }

  /** Die Frage des Zugs `zug` wartet, die Seite ist im Hintergrund: merken und den Titel markieren. Ein neuer Merker ersetzt den alten. */
  merke(zug: number): void {
    if (this.merker) this.uhr.clearTimeout(this.merker.frist);
    const frist = this.uhr.setTimeout(() => {
      if (this.loesche()) this.melde("[ptt] frage-rueckkehr zu grund=frist");
    }, this.fristMs);
    this.merker = { zug, um: this.uhr.jetzt(), frist };
    this.markiere();
    this.melde("[ptt] frage-rueckkehr gemerkt");
  }

  /** Merker und Titel zurücksetzen (Aufnahme, Frist, Rückkehr). `true`, wenn einer wartete. */
  loesche(): boolean {
    const war = this.merker !== null;
    if (this.merker) this.uhr.clearTimeout(this.merker.frist);
    this.merker = null;
    this.titelZurueck();
    return war;
  }

  /**
   * Die Seite meldet sich (sichtbar/Fokus). Unsichtbar: nichts. Sichtbar: der Titel geht zurück. Ohne Fokus bleibt der Merker für den
   * Fokus liegen. Sichtbar UND vorn: genau eine Entscheidung — der Merker ist danach verbraucht.
   */
  rueckkehr(l: RueckkehrLage): RueckkehrErgebnis {
    if (!this.merker || !l.sichtbar) return { tun: "nichts" };
    this.titelZurueck();
    if (!l.fokus) return { tun: "nichts" };
    const m = this.merker;
    this.loesche();
    const grund = this.sperre(m, l);
    return grund ? { tun: "zu", grund } : { tun: "versuchen", zug: m.zug };
  }

  private sperre(m: { zug: number; um: number }, l: RueckkehrLage): string | null {
    const alter = this.uhr.jetzt() - m.um;
    if (!(alter >= 0 && alter <= this.fristMs)) return "frist";
    if (l.zug !== m.zug || !l.zugFertig) return "neuer-zug";
    if (l.fertig) return "fertig";
    if (!l.ruhe) return "zustand";
    if (l.stimme) return "stimme";
    if (l.anruf) return "anruf";
    if (l.karteOffen) return "ja-karte-offen";
    if (l.entscheidungOffen) return "entscheidung";
    if (l.tippt) return "tippt";
    return null;
  }

  private markiere(): void {
    const jetzt = this.titel.lies();
    if (jetzt.startsWith(WARTET_PRAEFIX)) return;
    this.alterTitel = jetzt;
    this.titel.setze(WARTET_PRAEFIX + jetzt);
  }

  private titelZurueck(): void {
    const jetzt = this.titel.lies();
    if (jetzt.startsWith(WARTET_PRAEFIX)) this.titel.setze(this.alterTitel ?? jetzt.slice(WARTET_PRAEFIX.length));
    this.alterTitel = null;
  }
}
